import Backbone from 'backbone'
import FormView from 'scoreboard/components/form-view'
import template from 'scoreboard/components/http-request-form.hbs'
import LabeledToggle from 'scoreboard/components/labeled-toggle'
const {GET, HTTP_HEADERS, POST} = constants
import _ from 'lodash'
import ConfigurableList, {filterEmptyItems} from 'scoreboard/components/configurable-list'
import KeyValue from 'scoreboard/imports/rest/key-value'
import formHelper from 'scoreboard/components/form-helper'
import i18n from 'scoreboard/components/i18n'
import MarkedField from 'scoreboard/components/marked-field'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import urlValidator from 'scoreboard/components/validators/url-validator'
import constants from 'scoreboard/components/constants'
const {CONTENT_TYPES} = constants
import {
	decodeUrlParameters,
	decodeUrlParametersToKeyValuePairs,
	encodeHttpRequestHeaders, encodeUrlParameters, getHttpHeadersAsKeyValuePairs,
	getHttpHeadersAsObject
} from 'scoreboard/components/http-utils'

const HttpRequestForm = Backbone.View.extend({

	tagName: 'form',

	className: 'http-request-form',

	/**
	 * Form for specifying information to be sent along with an HTTP request,
	 * i.e. HTTP method, URL, request parameters, request headers, and
	 * request body
	 *
	 * @param {Object} options
	 * @param {String} [options.urlTitleMsgCode='admin.import.datasource.from.rest.url']
	 * @param {String} options.url used when we want to render the whole url as an input
	 * @param {Boolean} [options.useBaseUrl=false] used when we want to render a base url as text
	 * 									   		 and an endpoint as an input
	 * @param {String} options.urlSuffix used in the same case as options.baseUrl
	 * @param {'GET'|'POST'} options.httpMethod
	 * @param {String} options.urlParameters URL parameters in the format 'key=value&key=value'
	 * @param {String} options.httpRequestHeaders HTTP headers in the format 'Header-Name: Header-Value'
	 * @param {String} options.requestBody JSON body for the request
	 */
	initialize: function(options = {}) {
		this.options = options
		this.formView = new FormView({
			el: this.el,
			allowCleanSubmission: true,
			submit: () => this.submit(),
		})
		this.urlTitleMsgCode = _.get(this.options, 'urlTitleMsgCode',
			'admin.import.datasource.from.rest.url')
		this.useBaseUrl = _.get(this.options, 'useBaseUrl', false)
		this.state = {
			url: this.options.url,
			urlSuffix: this.options.urlSuffix,
			httpMethod: this.options?.httpMethod?.toLowerCase() ?? GET, // default to GET
			urlParameters: this.options.urlParameters,
			httpRequestHeaders: this.options.httpRequestHeaders,
			requestBody: this.options.requestBody
		}
	},

	render: function() {
		this.$el.html(template(this.context()))
		this.renderHttpMethodToggle()
		this.renderUrl()
		this._renderUrlParameters()
		this.renderHttpRequestHeaders()
		this.renderRequestBody()
		// TODO: uncomment when implementing #53510
		// this.renderFormData()

		if (!_.isNil(this.state.httpMethod)) {
			this.httpMethodToggle.val(this.state.httpMethod)
		}
		this.update()
		this.markRequiredFields()
		return this
	},

	context: function() {
		return {
			urlTitle: i18n.get(this.urlTitleMsgCode)
		}
	},

	/**
	 * Updates the DOM based on the user's previous selection. Updates the
	 * base URL, toggles field visibility, etc.
	 */
	update: function() {
		this.trigger('change')
		this.saveState()
		this.renderUrl()
		this.renderRequestBody()
	},

	renderHttpMethodToggle: function() {
		this.httpMethodToggle = new LabeledToggle({
			name: 'httpMethod',
			options: [
				// note the variable GET is lower case since it's an enum
				{ label: 'GET', value: GET },
				{ label: 'POST', value: POST }
			]
		})
		this.httpMethodToggle.on('change', this.update.bind(this))
		this.$('.http-method .toggle-container').append(this.httpMethodToggle.el)
		this.httpMethodToggle.render()
	},

	renderUrl: function() {
		this.$('.full-url').toggleClass('is-not-displayed', this.useBaseUrl)
		this.$('.api-base-url').toggleClass('is-not-displayed', !this.useBaseUrl)
		if (this.useBaseUrl) {
			this.$('.url-prefix').text(this.state.url)
			this.$('[name=urlSuffix').val(this.state.urlSuffix || '')
		} else {
			this.$('[name=url]').val(this.state.url || '')
		}
	},

	renderRequestBody: function() {
		if (!_.isNil(this.state.requestBody)) {
			this.$('[name=requestBody]').val(this.state.requestBody)
		}
		this.$('.request-body').toggleClass('is-not-displayed',
			this.state.httpMethod === GET)
	},

	_renderUrlParameters: function() {
		this.renderUrlParameters(this.state.urlParameters)
	},

	/**
	 * @param urlParametersString {string}
	 */
	renderUrlParameters: function(urlParametersString) {
		const urlParams = decodeUrlParametersToKeyValuePairs(urlParametersString)
		this.urlParametersList = this.createConfigurableList({
			containerEl: '.url-parameters-container',
			itemData: urlParams,
			addLabel: 'admin.import.datasource.from.rest.urlParameters.add'
		}).render()
	},

	getHttpHeaderItemData: function() {
		return getHttpHeadersAsKeyValuePairs(this.state.httpRequestHeaders)
	},

	renderHttpRequestHeaders: function() {
		this.httpRequestHeadersList = this.createConfigurableList({
			containerEl: '.http-headers-container',
			itemData: this.getHttpHeaderItemData(),
			addLabel: 'admin.import.datasource.from.rest.httpRequestHeaders.add'
		}).render()
	},

	getFormDataAsObjectFromRequestBody: function() {
		const httpHeaders = getHttpHeadersAsObject(this.state.httpRequestHeaders)
		const contentTypeHeader = httpHeaders[HTTP_HEADERS.CONTENT_TYPE]
		if (_.isNil(contentTypeHeader) ||
			contentTypeHeader !== CONTENT_TYPES.APPLICATION_X_WWW_FORM_URLENCODED ||
			_.isNil(this.state.requestBody)) return

		return decodeUrlParameters(this.state.requestBody)
	},

	renderFormData: function() {
		const formData = this.getFormDataAsObjectFromRequestBody()
		// don't display the form data section if there is no form data
		if (_.isNil(formData)) return

		const itemData = _.map(formData, (value, key) => ({ key, value }))
		this.formDataList = this.createConfigurableList({
			containerEl: '.form-data-container',
			itemData,
			addLabel: 'admin.import.datasource.from.rest.formData.add'
		}).render()
	},

	createConfigurableList: function({ containerEl, itemData, addLabel }) {
		const list = new ConfigurableList({
			itemViewCreator: itemDatum => new KeyValue(itemDatum),
			itemData,
			addLabel,
			allowLastItemDeletion: true
		})
		// #52672 emit change event when list items are added or deleted
		list.on('delete-list-item add-list-item', () => this.trigger('change'))
		this.$(containerEl).append(list.el)
		return list
	},

	readFromDom: function() {
		let fromDom = formHelper.readFromDom(this.el)
		delete fromDom.key
		delete fromDom.value
		const urlParameters = filterEmptyItems(this.urlParametersList.readFromDom())
		fromDom.urlParameters = encodeUrlParameters(urlParameters)
		const httpRequestHeaders = filterEmptyItems(this.httpRequestHeadersList.readFromDom())
		fromDom.httpRequestHeaders = encodeHttpRequestHeaders(httpRequestHeaders)
		fromDom.requestBody = fromDom.requestBody?.trim()
		return fromDom
	},

	markRequiredFields: function() {
		this.requiredFields = [
			new MarkedField({
				el: this.$('[name=url]'),
				validators: [requiredValidator, urlValidator]
			}).render()
		]
	},

	getState: function() {
		return _.extend(this.state || {}, this.readFromDom())
	},

	saveState: function() {
		this.state = this.getState()
	},

	submit: function() {
		this.saveState()
		this.trigger('submit', this.state)
		return Promise.resolve()
	}
})

export default HttpRequestForm

HttpRequestForm.__filename__ = 'components/http-request-form.js'
